/* eslint-disable */
import { cn } from "@/lib/utils";
import { ImageIcon } from "lucide-react";
import Image from "next/image";
import React, { Dispatch, SetStateAction, useState } from "react";

interface ImageHandlerProps {
  setImageUrls: Dispatch<SetStateAction<string[]>>;
  multiple?: boolean;
  imageUrls: string[];
  inputId: string;
}

const ImageHandler = ({
  imageUrls,
  setImageUrls,
  multiple = false,
  inputId,
}: ImageHandlerProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = event.target.files;
    if (selectedFiles) {
      const fileArray = Array.from(selectedFiles);
      setFiles((prevFiles) => [...prevFiles, ...fileArray]);
      await uploadFiles(fileArray);
    }
  };

  const uploadFiles = async (files: File[]) => {
    setUploading(true);
    setError(null);

    try {
      const imageUrls = await Promise.all(
        files.map(async (file) => {
          const formData = new FormData();
          formData.append("file", file);
          const bytes = await file.arrayBuffer();
          const base64File = Buffer.from(bytes).toString("base64"); // Encode file to base64

          const payload = {
            fileName: file.name || "file.jpg",
            fileType: file.type || "image/jpeg",
            fileContent: base64File, // Send base64-encoded file
          };
          const res = await fetch(
            `${process.env.NEXT_PUBLIC_PROFILE_SERVER_URL}/upload`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            }
          );

          if (!res.ok) throw new Error(await res.text());

          const resData = await res.json();
          return `${process.env.NEXT_PUBLIC_PROFILE_SERVER_URL}/files/${resData.filePath}`;
        })
      );
      setImageUrls((prevUrls) => [...prevUrls, ...imageUrls]);
    } catch (e: any) {
      setError(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const triggerFileInput = () => {
    const fileInput = document.getElementById(
      `${inputId}`
    ) as HTMLInputElement | null;
    if (fileInput) {
      fileInput.click();
    }
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {imageUrls.length > 0 &&
          imageUrls.map((file, index) => {
            return (
              <div key={index} className="">
                <Image
                  src={file}
                  alt={`uploaded preview ${index + 1}`}
                  height={1024}
                  width={1024}
                  className="object-cover h-20 w-20 rounded-md"
                />
              </div>
            );
          })}
      </div>
      <div
        className={cn(
          "h-5 w-5 relative group hover:cursor-pointer flex items-center justify-center ml-3"
        )}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center overflow-hidden p-4">
          <input
            type="file"
            name="file"
            onChange={handleFileChange}
            className="mb-2 hidden"
            multiple={multiple}
            id={inputId}
          />
          <button
            onClick={triggerFileInput}
            className="flex items-center p-2 rounded-md bg-gray-200/50 dark:bg-gray-900 hover:bg-gray-300"
            disabled={uploading}
          >
            <ImageIcon className="h-6 w-6" />
          </button>
        </div>
      </div>
      {error && <p className="text-red-500">{error}</p>}
    </div>
  );
};

export default ImageHandler;
